/**
 * ustvaAccess — geteilte Zugriffslogik für die USt-Voranmeldungs-Vorschau (M8,
 * `/dashboard/umsatzsteuer`).
 *
 * Single source of truth dafür, WER die UStVA-Sicht sehen und benutzen darf.
 * Wird von `config/navigation.ts` (Nav-Eintrag), `routes/dashboardRoutePaths.ts`
 * (Route-Registrierung) und `pages/UmsatzsteuerPage.tsx` (Inhalts-Gate)
 * gemeinsam genutzt — kein Copy-Paste der Flag-/Allowlist-Bedingung.
 * Muster: `config/travelExpensesAccess.ts` / `config/receiptScanAccess.ts`.
 *
 * Zwei Freischalt-Pfade (fail-closed — Default ist KEIN Zugriff):
 *   1. Beta-Allowlist: explizit gelistete Test-/Business-Account-UIDs sehen die
 *      Seite LIVE, unabhängig vom Tier und vom Env-Flag (kontrollierte Erprobung).
 *   2. Allgemeine Freigabe: `REACT_APP_USTVA_ENABLED==='true'` UND Ultra-Tier.
 *
 * Tier-Term (Ultra): Die Abo-Vergleichstabelle (`config/tiers.ts`) verkauft die
 * UStVA-Vorschau als Ultra-exklusiv (§1/CFO-Empfehlung), es fehlte bisher aber
 * ein Tier-Gate im Code — ein globaler Flag-Flip hätte das Feature allen
 * Nutzern (inkl. Free) gegeben. `canUseUstva` schließt diese Lücke: bei
 * allgemeiner Freigabe gilt `FLAG ∧ tier==='ultra'` (wie `canUseReceiptScan`/
 * `canUseBankReconcile`). Inert, solange das Flag live `false`/unset ist ⇒ nur
 * die Beta-Allowlist greift.
 *
 * Firebase-frei (nur `process.env` + der pure `Tier`-Typ) — importierbar aus
 * `routes/dashboardRoutePaths.ts`/`config/navigation.ts` (die selbst KEIN
 * Firebase ziehen dürfen).
 *
 * @module config/ustvaAccess
 */

import type { Tier } from './entitlements';

/**
 * Beta-Erprobung UStVA — hartkodiert (Muster `RECEIPT_SCAN_BETA_UIDS`), für die
 * kontrollierte Live-Erprobung durch Test- (b3DRR1…) und Business-Account
 * (70Tgnu…). Vor allgemeiner Freigabe (Flag = 'true' für alle Ultra-Nutzer)
 * wieder leeren.
 */
export const USTVA_BETA_UIDS: readonly string[] = [
  'b3DRR1Cj63cRjdJg5VCEUpBL7hk1',
  '70TgnuHwmDNI2LovagtcxXtIflo2',
];

/**
 * Allgemeines Feature-Flag. Fail-closed: nur bei explizitem `'true'` aktiv.
 * Modul-Konstante, einmalig beim Bundle-Load ausgewertet (dieselbe Env-Var, die
 * `UmsatzsteuerPage.tsx` bisher direkt als `USTVA_ENABLED` las).
 */
export const USTVA_FLAG: boolean = process.env.REACT_APP_USTVA_ENABLED === 'true';

/**
 * Route-Registrierungs-Gate (firebase-frei): die Route wird registriert, sobald
 * das Flag aktiv ist ODER die Beta-Allowlist nicht leer ist — unabhängig davon,
 * ob der aktuelle Nutzer sie sehen darf (die feine Per-Nutzer-Entscheidung
 * trifft `canUseUstva` im Seiten-/Nav-Gate). Muster `TRAVEL_EXPENSES_ROUTE_ENABLED`.
 */
export const USTVA_ROUTE_ENABLED: boolean = USTVA_FLAG || USTVA_BETA_UIDS.length > 0;

/**
 * Entscheidet fail-closed, ob ein Nutzer die UStVA-Vorschau sehen/benutzen darf.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined,
 *              solange noch nicht eingeloggt).
 * @param tier - effektiver Tier des Nutzers (`resolveTier(sellerData)` aus
 *               `entitlements.ts`). UStVA ist ab GA ein Ultra-Feature.
 * @returns `true`, wenn die UID auf der Beta-Allowlist steht ODER das Flag aktiv
 *          ist UND der Nutzer den Ultra-Tier hat. Sonst `false`.
 */
export function canUseUstva(uid: string | null | undefined, tier: Tier): boolean {
  if (uid && USTVA_BETA_UIDS.includes(uid)) {
    return true;
  }
  return USTVA_FLAG && tier === 'ultra';
}

// ─── UStVA-XML-Export (M-U7, §15) — separates Sub-Feature-Gate ────────────────────

/**
 * Sub-Feature-Flag für den ELSTER-XML-Export (M-U7). Fail-closed: nur bei explizitem
 * `'true'` aktiv. Bewusst getrennt vom Vorschau-Flag (`USTVA_FLAG`) — der Export ist
 * ein eigenständiger, separat flippbarer Reifegrad hinter dem Betriebs-Gate G-U4
 * (Mein-ELSTER-Upload-Beweis je KZ / XSD). Solange live `false`/unset ⇒ nur die
 * Beta-Allowlist exportiert.
 */
export const USTVA_XML_EXPORT_FLAG: boolean =
  process.env.REACT_APP_USTVA_XML_EXPORT_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob ein Nutzer den UStVA-XML-Export nutzen darf (§15).
 *
 * Export setzt den Vorschau-Zugriff (`canUseUstva`) VORAUS UND ist zusätzlich separat
 * flippbar: erlaubt ist er für Beta-UIDs (unabhängig vom Sub-Flag) oder — nach dem
 * GA-Flip — für jeden Vorschau-Berechtigten, sobald `USTVA_XML_EXPORT_FLAG` aktiv ist.
 *
 * @param uid - Firebase-Auth-UID (oder null/undefined vor dem Login).
 * @param tier - effektiver Tier (`resolveTier(sellerData)`).
 * @returns `true` nur, wenn der Nutzer die Vorschau sehen darf UND (Beta-UID ODER
 *          Sub-Flag aktiv). Sonst `false`.
 */
export function canExportUstvaXml(uid: string | null | undefined, tier: Tier): boolean {
  if (!canUseUstva(uid, tier)) {
    return false;
  }
  return (uid != null && USTVA_BETA_UIDS.includes(uid)) || USTVA_XML_EXPORT_FLAG;
}
